/**
 * Commission reconciliation (added — see docs/BENCHMARKS.md).
 *
 * The variance column is the whole point: what the insurer says they paid, minus what
 * the book says was earned. Anything non-zero is money to chase.
 */

import { Receipt, TriangleAlert, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { SettlementDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EntityCell } from '@/components/common/entity-avatar';
import { EnumBadge } from '@/components/common/enum-badge';
import { EmptyState, PageHeader } from '@/components/common/page-header';
import { StatTile, StatTileRow } from '@/components/common/stat-tile';
import { settlementsApi, useDashboardSummary, useProducerSplits } from '@/lib/api/domains';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';
import { cn } from '@/lib/utils';

export function CommissionsPage() {
  const { t, money, date, number } = useI18n();

  const list = useListState({ defaultSortBy: 'statementDate', defaultSortOrder: 'desc' });
  const { data, isLoading, isFetching, error, refetch } = settlementsApi.useList(list.params);
  const summary = useDashboardSummary({});
  const splits = useProducerSplits({});

  const columns: DataColumn<SettlementDto>[] = [
    {
      id: 'statementReference',
      header: t('commission.reference'),
      meta: { headerLabel: t('commission.reference') },
      cell: ({ row }) => <span className="font-medium tabular">{row.original.statementReference}</span>,
    },
    {
      id: 'insuranceCompanyName',
      header: t('policy.company'),
      meta: { headerLabel: t('policy.company') },
      cell: ({ row }) => <EntityCell name={row.original.insuranceCompanyName} />,
    },
    {
      id: 'period',
      header: t('common.dateRange'),
      meta: { numeric: true, hideBelow: 'lg', headerLabel: t('common.dateRange') },
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {date(row.original.periodFrom)} – {date(row.original.periodTo)}
        </span>
      ),
    },
    {
      id: 'matchedPolicyCount',
      header: t('commission.matched'),
      meta: { align: 'center', numeric: true, hideBelow: 'md', headerLabel: t('commission.matched') },
      cell: ({ row }) => number(row.original.matchedPolicyCount),
    },
    {
      id: 'expectedAmount',
      header: t('commission.expected'),
      meta: { align: 'right', numeric: true, headerLabel: t('commission.expected') },
      cell: ({ row }) => money(row.original.expectedAmount),
    },
    {
      id: 'declaredAmount',
      header: t('commission.declared'),
      meta: { align: 'right', numeric: true, headerLabel: t('commission.declared') },
      cell: ({ row }) => money(row.original.declaredAmount),
    },
    {
      id: 'varianceAmount',
      header: t('commission.variance'),
      meta: { align: 'right', numeric: true, headerLabel: t('commission.variance') },
      cell: ({ row }) => {
        const variance = Number(row.original.varianceAmount);
        // Zero variance is the quiet, expected state and gets no colour at all.
        const tone = Math.abs(variance) < 0.01 ? '' : variance < 0 ? 'text-critical' : 'text-good';
        return (
          <span className={cn('font-medium', tone)}>
            {variance > 0 ? '+' : ''}
            {money(row.original.varianceAmount)}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: t('policy.status'),
      meta: { headerLabel: t('policy.status') },
      cell: ({ row }) => <EnumBadge enum="settlementStatus" value={row.original.status} size="sm" />,
    },
  ];

  return (
    <>
      <PageHeader title={t('commission.title')} description={t('commission.subtitle')} />

      <StatTileRow className="mb-5 xl:grid-cols-3">
        <StatTile
          label={t('dashboard.totalCommission')}
          value={money(summary.data?.totalCommission)}
          icon={Receipt}
          isLoading={summary.isLoading}
        />
        <StatTile
          label={t('dashboard.unreconciled')}
          value={money(summary.data?.unreconciledCommission)}
          // The old label ("Ασυμφώνητες προμήθειες") read as "settlement variances", which
          // is a different and much smaller number. This is commission the agency has
          // earned but the carrier has not yet statemented.
          hint={t('dashboard.unreconciledHint')}
          tone={Number(summary.data?.unreconciledCommission ?? 0) > 0 ? 'warning' : 'default'}
          isLoading={summary.isLoading}
        />
        <StatTile
          label={t('dashboard.receivables')}
          value={money(summary.data?.outstandingReceivables)}
          isLoading={summary.isLoading}
        />
      </StatTileRow>

      {/*
        The disclaimer on every figure above it, and it only shows while it is true.
        Commission is calculated on the ΚΑΘΑΡΑ alone; a policy with none earns 0,00 € by
        definition. Without this line the total reads as the agency's income and is not — it is
        the income of the part of the book that has had its net premium entered.
      */}
      {(summary.data?.policiesMissingNetPremium ?? 0) > 0 && (
        <Link
          to="/policies?missingNetPremium=true"
          className="border-border-strong bg-card text-serious hover:bg-accent mb-5 flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-[13px] transition-colors duration-150"
        >
          <TriangleAlert className="size-4 shrink-0" />
          <span>
            {t('commission.missingNet', { count: number(summary.data!.policiesMissingNetPremium) })}
          </span>
        </Link>
      )}

      <Tabs defaultValue="settlements">
        <TabsList className="mb-4">
          <TabsTrigger value="settlements">{t('commission.settlement')}</TabsTrigger>
          <TabsTrigger value="producers">
            <Users /> {t('commission.producerSplits')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settlements">
          <DataTable
            columns={columns}
            rows={data?.data}
            total={data?.meta.total}
            listState={list}
            isLoading={isLoading}
            isFetching={isFetching}
            error={error}
            onRetry={refetch}
            getRowId={(settlement) => settlement.id}
            emptyTitle={t('common.empty')}
            emptyHint={t('commission.subtitle')}
          />
        </TabsContent>

        <TabsContent value="producers">
          <Card>
            <CardHeader>
              <div className="min-w-0">
                <CardTitle>{t('commission.producerSplits')}</CardTitle>
                {/* This table counts collected policies only, while the identically-named
                    table in Reports counts the whole active book. Both are right; without
                    saying which, the two pages look like they contradict each other. */}
                <CardDescription>{t('commission.producerSplitsHint')}</CardDescription>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {splits.isLoading ? (
                <Skeleton className="m-5 h-40" />
              ) : !splits.data?.length ? (
                <EmptyState title={t('common.empty')} compact />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('client.agent')}</TableHead>
                      <TableHead className="text-center">{t('commission.share')}</TableHead>
                      <TableHead className="text-right">{t('report.policyCount')}</TableHead>
                      <TableHead className="text-right">{t('dashboard.totalCommission')}</TableHead>
                      <TableHead className="text-right">{t('commission.producerShare')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {splits.data.map((row) => (
                      <TableRow key={row.agentId}>
                        <TableCell className="py-2.5 font-medium">{row.agentName}</TableCell>
                        <TableCell className="py-2.5 text-center">
                          <Badge tone="outline" size="sm">
                            {row.sharePercentage}%
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2.5 text-right tabular">{row.policyCount}</TableCell>
                        <TableCell className="text-muted-foreground py-2.5 text-right tabular">
                          {money(row.agencyCommission)}
                        </TableCell>
                        <TableCell className="py-2.5 text-right font-semibold tabular">
                          {money(row.producerShare)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
